/* eslint-disable @typescript-eslint/no-explicit-any */
import {shallowEqual, useSelector, useDispatch} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';
import {Box, Skeleton} from '@mui/material';
import jobDetailService from '@jobseeker/common-core/services/jobdetail.service';
import {profileService} from '@jobseeker/common-core/services';
import {showErrorToast} from '@jobseeker/common-core/utils';
import dynamic from 'next/dynamic';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME;
const isAppIIMJobs = PLATFORM === 'iimjobs';
const isAppHirist = PLATFORM === 'hirist';

type ScreeningProfileData = {
  currentLocation?: number;
  expectedSalary?: number;
  currentSalary?: number;
  noticePeriod?: number;
  skill?: string[];
};

type ScreeningJobSeekerProps = {
  seekerId: number;
  associationPublished: number;
  submitCallback: (params?: {
    screeningProfileData?: ScreeningProfileData;
  }) => void;
  errorCallback: (err: any) => void;
  mode?: 'prod';
  loader?: React.ReactNode;
  assessmentLoginToken?: string;
  profileData?: {
    currentLocation?: number;
    expectedSalary?: number;
    currentSalary?: number;
    noticePeriod?: number;
  };
};

const ScreeningJobSeeker = dynamic(
  async () => {
    const module = isAppIIMJobs
      ? await import('@iimjobs/screening')
      : await import('@hirist/screening');
    return module.ScreeningJobSeeker as React.FC<ScreeningJobSeekerProps>;
  },
  {
    ssr: false,
  }
);

const ScreeningView = ({containerStyles, onNext, handleApplyJob}: any) => {
  const dispatch = useDispatch();
  const theme = useWrappedTheme();
  const detail = useSelector(
    (state: AppState) => state.jobDetail,
    shallowEqual
  );
  const user = useSelector((state: AppState) => state.user, shallowEqual);
  const {token = ''} = useSelector(
    (state: AppState) => state.auth.authInfo,
    shallowEqual
  );
  const onError = (err: any) => {
    if (err.code === 'E01') {
      showErrorToast(`${err.msg}, Skipping to Next Step`, err);

      onNext();
    } else {
      showErrorToast(err.msg, err);
    }
  };
  const demo = false;
  const onSuccess = async (params?: {
    screeningProfileData?: ScreeningProfileData;
  }) => {
    try {
      const screeningProfileData = params?.screeningProfileData;
      if (screeningProfileData) {
        const {
          currentLocation,
          expectedSalary,
          currentSalary,
          noticePeriod,
          skill,
        } = screeningProfileData;

        let infoString = '';
        const pdPayload: Record<string, any> = {
          id: user.profile.id,
        };

        if (currentLocation != null) {
          pdPayload.currentLocation = currentLocation;
          infoString += 'a5';
        }
        if (expectedSalary != null) {
          pdPayload.expectedSalary = expectedSalary;
          infoString += 'i1';
        }
        if (currentSalary != null) {
          pdPayload.currentSalary = currentSalary;
          infoString += 'h9';
        }
        if (noticePeriod != null) {
          pdPayload.noticePeriod = noticePeriod;
          infoString += 'b2';
        }
        if (skill?.length) {
          const existingSkills = user.profile.skills
            ? Object.values(user.profile.skills).map(s => s.tags)
            : [];
          const existingLower = existingSkills.map(s => s.toLowerCase());
          const newSkills = skill.filter(
            s => !existingLower.includes(s.toLowerCase())
          );

          if (newSkills.length) {
            pdPayload.skills = [...existingSkills, ...newSkills];
            infoString += 's1';
          }
        }

        pdPayload.infoString = infoString;
        pdPayload.requiredString = infoString;

        await profileService.updateProfile(
          {pd: pdPayload},
          '',
          5,
          isAppIIMJobs ? '1.0.12' : '1.0.47'
        );
      }

      await jobDetailService.markAssessmentComplete(detail.id);
      // enqueueSnackbar('Your form has been submitted', {variant: 'success'});
      dispatch({
        type: 'job/jobdetail/set_jobdetail',
        payload: {...detail, assessmentCompleted: 1},
      });
      if (isAppIIMJobs) {
        onNext();
      } else {
        handleApplyJob();
      }
    } catch (error) {
      showErrorToast('something went wrong', error);
    }
  };

  const randomWidth = () => {
    const min = 40,
      max = 100;
    const randomNumber = Math.floor(Math.random() * (max - min + 1) + min);
    return `${randomNumber}%`;
  };

  return (
    <Box
      sx={{
        ...containerStyles,
        pt: {xxs: 1, md: 2},
        pb: 5,
        px: {xxs: 1, md: 2},
        ...(isAppHirist && {
          '*': {
            fontFamily: 'Nunito Sans !important',
          },
          button: {
            bgcolor: `${theme.palette.primary.main} !important`,
            color: 'white !important',
            border: 'none !important',
            borderRadius: '4px !important',
          },
        }),
      }}
    >
      <ScreeningJobSeeker
        seekerId={demo ? 2207827 : user.profile.id}
        associationPublished={demo ? 615335 : detail.id}
        submitCallback={onSuccess}
        errorCallback={onError}
        // mode={process.env.NODE_ENV === 'production' ? 'prod' : undefined} // TODO: Revert this before going live
        mode="prod"
        profileData={{
          currentLocation: user.profile.locations?.current?.id,
          expectedSalary: user.profile.expectedSalary,
          currentSalary: user.profile.currentSalary,
          noticePeriod: user.profile.noticePeriod
            ? parseInt(user.profile.noticePeriod)
            : undefined,
        }}
        loader={
          <Box minWidth={'100%'} width={'100%'}>
            {[...Array(20)].map((_, i) => (
              <Skeleton
                key={`screening-ques-${i}`}
                variant="rounded"
                width={randomWidth()}
                height={21}
                sx={{m: 2, transition: 'all 0.3s ease'}}
              />
            ))}
          </Box>
        }
        assessmentLoginToken={token}
      />
    </Box>
  );
};
export default ScreeningView;
